import { useState, useEffect } from "react";
import {
  Typography, Card, List, Button, Tag, Empty, Spin, Alert, Space, Divider,
} from "antd";
import {
  FilePdfOutlined, DownloadOutlined, CalendarOutlined, BellOutlined,
} from "@ant-design/icons";
import { getNotaryDocuments, getAnnouncements, logPortalActivity } from "../../api/portalService";

const { Title, Text, Paragraph } = Typography;

const formatBytes = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PortalDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      getNotaryDocuments().then(({ data }) => setDocuments(data.results || [])),
      getAnnouncements().then(({ data }) => setAnnouncements(data.results || [])),
    ])
      .catch(() => setError("No se pudieron cargar los datos."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />
      )}

      {/* Anuncios */}
      {announcements.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <BellOutlined style={{ fontSize: 20, color: "#1d3557" }} />
            <Title level={4} style={{ margin: 0, color: "#1d3557" }}>
              Avisos de la Notaría
            </Title>
          </div>
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            {announcements.map((ann) => (
              <Card
                key={ann.id}
                style={{
                  borderRadius: 8,
                  border: "1px solid #bde0ff",
                  background: "#f0f8ff",
                }}
                bodyStyle={{ padding: "16px 20px" }}
              >
                <Text strong style={{ fontSize: 15, display: "block", marginBottom: 6, color: "#1d3557" }}>
                  {ann.title}
                </Text>
                <Paragraph style={{ margin: 0, whiteSpace: "pre-line", color: "#444" }}>
                  {ann.content}
                </Paragraph>
                {ann.createdAt && !isNaN(new Date(ann.createdAt)) && (
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: "block" }}>
                    {new Date(ann.createdAt).toLocaleDateString("es-CL", { day: "2-digit", month: "long", year: "numeric" })}
                  </Text>
                )}
              </Card>
            ))}
          </Space>
          <Divider />
        </div>
      )}

      {/* Documentos */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0, color: "#1d3557" }}>
          Documentos disponibles
        </Title>
        <Text type="secondary">
          Documentos proporcionados por la Notaría Juan Carlos Cabezas Cepeda.
        </Text>
      </div>

      {!loading && documents.length === 0 && !error && (
        <Empty
          description="No hay documentos disponibles por el momento."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      <List
        grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2 }}
        dataSource={documents}
        renderItem={(doc) => (
          <List.Item>
            <Card
              hoverable
              style={{ borderRadius: 8, border: "1px solid #e8e8e8" }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 8,
                  background: "#fff1f0", display: "flex",
                  alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <FilePdfOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 15, display: "block", marginBottom: 4 }}>
                    {doc.title}
                  </Text>
                  {doc.description && (
                    <Paragraph
                      type="secondary"
                      style={{ fontSize: 13, margin: "0 0 8px" }}
                      ellipsis={{ rows: 2 }}
                    >
                      {doc.description}
                    </Paragraph>
                  )}
                  <Space size={8} wrap>
                    {doc.fileSize && (
                      <Tag style={{ fontSize: 12 }}>{formatBytes(doc.fileSize)}</Tag>
                    )}
                    {doc.uploadedAt && (
                      <Tag icon={<CalendarOutlined />} style={{ fontSize: 12 }}>
                        {new Date(doc.uploadedAt).toLocaleDateString("es-CL")}
                      </Tag>
                    )}
                  </Space>
                </div>
              </div>

              <Button
                type="primary"
                icon={<DownloadOutlined />}
                href={doc.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                block
                onClick={() => logPortalActivity('open_pdf', { documentId: doc.id, title: doc.title, fileName: doc.fileName })}
                style={{
                  marginTop: 16, height: 40,
                  background: "#1d3557", borderColor: "#1d3557",
                }}
              >
                Descargar
              </Button>
            </Card>
          </List.Item>
        )}
      />
    </div>
  );
};

export default PortalDocuments;
